import { eventTypes } from './event-types';
import { isGtmDataLayerPushEnabled } from '../../gtm-config';
import type {
  Route,
  UserData,
  ScrollData,
  PortfolioCreationData,
  TransactionCompletedData,
  SearchData,
  GtmPush,
  MenuClickData,
  BurgerMenuToggleData,
  FeedClickData,
  CreatePostData,
  FollowActiveData,
  FollowInvestorData,
  SendMessageInvestorData,
  ClickAlertData,
  ClickTopPortfoliosData,
  InteractionArticleData,
  PostImpressionData,
  PortfolioInteractionData,
  InteractionEventData,
  OnboardingNextStepData,
  LoginData,
  RegisterData,
} from './gtm-data-types';

/**
 * Gets device and platform information
 */
function getDeviceInfo() {
  const ua = window.navigator.userAgent;
  const mobile = {
    android: /Android/i.test(ua),
    ios: /iPhone|iPad|iPod/i.test(ua),
    any: /Android|iPhone|iPad|iPod/i.test(ua),
  };

  const tablet = /iPad|Android(?!.*Mobile)/i.test(ua);

  return {
    is_mobile: mobile.any,
    device_type: tablet ? 'tablet' : mobile.any ? 'mobile' : 'desktop',
    platform: mobile.ios ? 'ios' : mobile.android ? 'android' : 'web',
  };
}

/**
 * Gets or creates a session ID
 */
function getSessionId() {
  let sessionId = sessionStorage.getItem('gtm_session_id');
  if (!sessionId) {
    sessionId = `sess_${Math.random().toString(36).substr(2, 15)}`;
    sessionStorage.setItem('gtm_session_id', sessionId);
  }
  return sessionId;
}

/**
 * This function raises a GTM event.
 * @param eventType - the type of the event to raise
 * @param eventParameters - the event parameters
 * @returns void
 */
function raiseGtmEvent(
  eventType: string,
  eventParameters: Record<string, unknown>
) {
  // check if GTM is enabled and present
  if (!isGtmDataLayerPushEnabled()) {
    return;
  }

  // Using type assertion to access GTM functionality
  // This is safe because we control the GTM initialization
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gtm = (window as any)?.$nuxt?.$gtm as GtmPush | undefined;
  if (!gtm?.push) {
    console.error('GTM is not defined');
    return;
  }

  // Checking if amplitude script has loaded - if GTM is used in debug mode, it's expected that it doesn't load amplitude
  // So this check is kept for production only, so that we can still debug what's pushed to the datalayer on dev env
  if (process.env.NODE_ENV === 'production') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const amplitude = (window as any)?.amplitude;

    const isAmplitudeReady = typeof amplitude?.logEvent === 'function';

    if (!isAmplitudeReady) {
      // Debug mode GTM doesn't load amplitude as it's not sending any event from its datalayer
      console.error('Amplitude is not defined.');
      return;
    }
  }

  try {
    gtm.push({
      event: eventType,
      ...eventParameters,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error pushing event to GTM:', error);
  }
}

/**
 * Raises an event when the user navigates to a new page
 * @param route - the current route object
 * @param previousPath - the previous path
 */
function raisePageViewEvent(route: Route, previousPath?: string) {
  const deviceInfo = getDeviceInfo();

  raiseGtmEvent(eventTypes.pageView, {
    // @ts-expect-error - titleKey is not defined in the type
    page_title: route?.meta?.titleKey || 'Skade',
    page_location: window.location.href,
    page_path: route?.fullPath,
    page_referrer: previousPath || document.referrer,
    page_language: (window as Window)?.$nuxt?.$i18n?.locale,
    session_id: getSessionId(),
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    ...deviceInfo,
  });
}

/**
 * Raises a single event per session (browser tab) to identify the user
 * @param userData - user data to be sent with the event
 */
function raiseUserIdentifyEvent(userData: UserData) {
  raiseGtmEvent(eventTypes.userIdentify, {
    user_id: userData?.userId,
    user_id_type: userData?.accountProvider,
    user_email: userData?.email,
    user_first_name: userData?.firstName,
    user_last_name: userData?.lastName,
    user_full_name: userData?.name,
    user_registration_date: userData?.createdDateUtc,
  });
}

/**
 * Raises an event when the user scrolls on the page up to a certain threshold
 * The threshold is set in the gtm-events store
 * @param scrollData - scroll tracking data
 */
function raiseScrollEvent(scrollData: ScrollData) {
  raiseGtmEvent(eventTypes.scroll, {
    scroll_threshold: `${scrollData.threshold}%`,
    page_path: scrollData.path,
    page_title: scrollData.title,
  });
}

/**
 * Raises an event when a search is performed in the main search
 * @param searchData - search data
 */
function raiseSearchEvent(searchData: SearchData) {
  raiseGtmEvent(eventTypes.search, searchData);
}

/**
 * Raises an event when a portfolio is successfully created
 * @param portfolioData - portfolio creation data
 */
function raisePortfolioCreationEvent(portfolioData: PortfolioCreationData) {
  raiseGtmEvent(eventTypes.portfolioCreation, portfolioData);
}

/**
 * Raises an event when a portfolio operation is successfully created
 * @param transactionData - transaction data
 */
function raiseTransactionCompletedEvent(
  transactionData: TransactionCompletedData
) {
  raiseGtmEvent(eventTypes.transactionCompleted, transactionData);
}

/**
 * Raises an event when the search bar is clicked
 */
function raiseClickSearchBarEvent() {
  raiseGtmEvent(eventTypes.clickSearchBar, {});
}

/**
 * Raises an event when any navigation item is clicked
 * in the main menu (mobile & desktop sidebar, top navbar, mobile bottom navbar)
 * @param text - The text of the clicked menu item
 */
function raiseClickMenuPrincipalEvent(menuData: MenuClickData) {
  raiseGtmEvent(eventTypes.clickMenuPrincipal, menuData);
}

/**
 * Raises an event when the burger menu is toggled
 * @param burgerMenuData - The state of the burger menu (Open or Closed)
 */
function raiseToggleMenuBurgerEvent(burgerMenuData: BurgerMenuToggleData) {
  raiseGtmEvent(eventTypes.toggleMenuBurger, burgerMenuData);
}

/**
 * Raises an event when a feed view is clicked
 * @param feedData - The feed data including the feed name
 */
function raiseClickViewFeedEvent(feedData: FeedClickData) {
  raiseGtmEvent(eventTypes.clickViewFeed, feedData);
}

/**
 * Raises an event when a post is created
 * @param postData - The post data including name, links and tags
 */
function raiseCreatePostEvent(postData: CreatePostData) {
  raiseGtmEvent(eventTypes.createPost, postData);
}

/**
 * Raises an event when a user follows an active (stock, crypto, etc)
 * @param followData The follow data including active name and category
 */
function raiseFollowActiveEvent(followData: FollowActiveData) {
  raiseGtmEvent(eventTypes.followActive, followData);
}

/**
 * Raises an event when a user follows an investor
 * @param followData The follow data including investor name and type
 */
function raiseFollowInvestorEvent(followData: FollowInvestorData) {
  raiseGtmEvent(eventTypes.followInvestor, followData);
}

/**
 * Raises an event when a user sends a message to an investor
 * @param messageData The message data including sender and receptor names
 */
function raiseSendMessageInvestorEvent(messageData: SendMessageInvestorData) {
  raiseGtmEvent(eventTypes.sendMessageInvestor, messageData);
}

/**
 * Raises an event when a user clicks on an alert
 * @param alertData The alert data including active name and category
 */
function raiseClickAlertEvent(alertData: ClickAlertData) {
  raiseGtmEvent(eventTypes.clickAlert, alertData);
}

/**
 * Raises an event when a user clicks on a top portfolio
 * @param portfolioData The portfolio data including name and position
 */
function raiseClickTopPortfoliosEvent(portfolioData: ClickTopPortfoliosData) {
  raiseGtmEvent(eventTypes.clickTopPortfolios, portfolioData);
}

/**
 * Raises an event when a user interacts with an article
 * @param interactionData The interaction data including article details and interaction type
 */
function raiseInteractionArticleEvent(interactionData: InteractionArticleData) {
  raiseGtmEvent(eventTypes.interactionArticle, interactionData);
}

/**
 * Raises an event when a post is viewed by a user
 * @param impressionData The impression data including post ID and author
 */
function raisePostImpressionEvent(impressionData: PostImpressionData) {
  raiseGtmEvent(eventTypes.postImpression, impressionData);
}

/**
 * Raises an event when a user interacts with a portfolio
 * @param interactionData The interaction data including portfolio details and interaction type
 */
function raisePortfolioInteractionEvent(
  interactionData: PortfolioInteractionData
) {
  raiseGtmEvent(eventTypes.portfolioInteraction, interactionData);
}

/**
 * Raises an event when a user interacts with content
 * @param interactionData The interaction data including category, action and post name
 */
function raiseInteractionEvent(interactionData: InteractionEventData) {
  raiseGtmEvent(eventTypes.interaction, interactionData);
}

/**
 * Raises an event when a user completes an onboarding step
 * @param interactionData The interaction data including category, action and post name
 */
function raiseOnboardingNextStepEvent(
  onboardingNextStepData: OnboardingNextStepData
) {
  raiseGtmEvent(eventTypes.onboarding, onboardingNextStepData);
}

/**
 * Raises an event when a user completes an onboarding step
 * @param interactionData The interaction data including category, action and post name
 */
function raiseLoginEvent(loginData: LoginData) {
  raiseGtmEvent(eventTypes.login, loginData);
}

/**
 * Raises an event when a user registers from the authProvider
 * @param registerData The interaction data including email and username
 */
function raiseRegisterEvent(registerData: RegisterData) {
  raiseGtmEvent(eventTypes.register, registerData);
}

/**
 * The event manager object for Google Tag Manager (GTM).
 */
export const gtmEventManager = {
  raisePageViewEvent,
  raiseUserIdentifyEvent,
  raiseScrollEvent,
  raisePortfolioCreationEvent,
  raiseTransactionCompletedEvent,
  raiseSearchEvent,
  raiseClickSearchBarEvent,
  raiseClickMenuPrincipalEvent,
  raiseToggleMenuBurgerEvent,
  raiseClickViewFeedEvent,
  raiseCreatePostEvent,
  raiseFollowActiveEvent,
  raiseFollowInvestorEvent,
  raiseSendMessageInvestorEvent,
  raiseClickAlertEvent,
  raiseClickTopPortfoliosEvent,
  raiseInteractionArticleEvent,
  raisePostImpressionEvent,
  raisePortfolioInteractionEvent,
  raiseInteractionEvent,
  raiseOnboardingNextStepEvent,
  raiseLoginEvent,
  raiseRegisterEvent,
};
